// "Sunset play": a still lake at sunset → the sky mirrored, tap the water → the colours wrap the room, reach for the sky → blues meet reds round the dark sun → the painting.
export const sunsetPlay = {
  slug: "sunset-play",
  duration: 28,
  stages: [
    { id: "lake", at: 0, hint: "a still lake at sunset" },
    { id: "mirror", at: 5.5, hint: "tap the water" },
    { id: "wrap", at: 12, hint: "reach for the sky" },
    { id: "play", at: 18, hint: "" },
  ],
  line: { text: "As above, so below: you can't touch a sunset, you can only experience it.", from: 14, to: 27.5, pos: "low" },
  touch: { kind: "sunset", from: 5.5, until: 21 },
  // painted panorama: the still lake mirroring blues and reds round the dark sun
  layers: [
    { src: "/arcs/sunsetplay/lake.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, water: 0.42, opacity: [[0, 1], [5.5, 0.7], [21, 0.7], [22.5, 1]], show: [-1, 0, 99, 99] },
  ],
  ambient: { scene: "pond", until: 99, then: [] },
  look: { start: 0.0, lift: [12, 15], end: 0.12, yaw: [[0, 0], [12, 0], [15, -0.4], [19, 0.5], [22, 0], [28, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "sunset", pos: "low" },
  speed: [[0, 1], [28, 1]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface;
uniform vec4 uRip[6];
uniform vec4 uMark[8];
varying vec3 vPos;
${"__NOISE__"}
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
const float HZ = 1.35;
// the sky at angle th and height y: broad horizontal strokes of reds and pinks, the cool side rising later
vec3 sky(float th, float y){
  float X = th * uRadius;
  float s = snoise(vec3(X * 0.32, y * 2.2, uT * 0.03)) * 0.5 + 0.5;
  float s2 = snoise(vec3(X * 0.9, y * 5.0, 2.0 + uT * 0.05)) * 0.5 + 0.5;
  vec3 c = mix(vec3(0.99, 0.64, 0.36), vec3(0.96, 0.5, 0.56), smoothstep(HZ, HZ + 1.6, y + 0.4 * s));
  c = mix(c, vec3(0.64, 0.14, 0.24), smoothstep(HZ + 1.0, HZ + 4.2, y + 0.8 * s));
  c *= 0.9 + 0.16 * s2;
  float play = smoothstep(16.0, 22.0, uT);
  float edge = mix(-3.4, -0.18, play) + 0.45 * (s - 0.5) + 0.2 * sin(y * 1.3 + uT * 0.4);
  vec3 cool = mix(vec3(0.32, 0.2, 0.36), vec3(0.33, 0.31, 0.51), s2) * (0.9 + 0.2 * s);
  c = mix(c, cool, 1.0 - smoothstep(edge - 0.35, edge + 0.35, th));
  // the sun sinking to the horizon, darkening as night comes in
  float sy = mix(2.7, HZ + 0.32, smoothstep(0.0, 12.0, uT));
  float sd = length(vec2(X, (y - sy) * 1.1));
  float dark = smoothstep(14.5, 16.5, uT);
  c += vec3(1.0, 0.72, 0.4) * exp(-sd * 1.4) * 0.35 * (1.0 - 0.6 * dark);
  vec3 sun = mix(vec3(1.0, 0.8, 0.5), vec3(0.2, 0.06, 0.14), dark);
  c = mix(c, sun, 1.0 - smoothstep(0.38, 0.42, sd));
  return c;
}
// reaching toward the sky pushes its colours gently away from the touch
vec2 drift(vec3 p, float th, float y){
  vec2 off = vec2(0.0);
  for (int i = 0; i < 8; i++){
    vec4 m = uMark[i];
    if (m.w <= 0.0) continue;
    vec2 d = vec2((th - atan(m.x, -m.z)) * uRadius, y - m.y);
    float k = smoothstep(0.0, 3.5, uT - m.w) * exp(-length(d) * 0.7);
    off += normalize(d + 0.0001) * k * 0.9;
  }
  return off;
}
float ripples(vec2 xz){
  float w = 0.0;
  for (int i = 0; i < 6; i++){
    vec4 r = uRip[i];
    if (r.w <= 0.0) continue;
    float age = uT - r.z;
    if (age < 0.0) continue;
    float d = distance(xz, r.xy);
    w += sin(d * 13.0 - age * 6.0) * exp(-age * 0.7) * exp(-abs(d - age * 1.5) * 2.5) * r.w;
  }
  return w;
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float wrap = smoothstep(12.0, 16.5, uT);
  float settle = smoothstep(21.5, 26.5, uT);
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float th = atan(vPos.x, -vPos.z);
    float y = vPos.y;
    vec2 dv = drift(vPos, th, y);
    if (y > HZ) {
      col = sky(th + dv.x / uRadius, y + dv.y);
    } else {
      // as above, so below: the sky shimmering in still water
      float depth = HZ - y;
      float sh = sin(y * 38.0 + uT * 2.2 + n * 5.0) * 0.05 * depth + sin(th * uRadius * 3.0 + uT) * 0.01;
      col = sky(th + (sh + dv.x) / uRadius, HZ + depth * 1.05 + dv.y) * 0.82 + vec3(0.02, 0.0, 0.05);
      col += vec3(1.0, 0.85, 0.7) * smoothstep(0.92, 1.0, sin(y * 60.0 + th * 9.0 + uT * 1.8)) * 0.08 * (1.0 - depth / HZ);
    }
    local = vec2(th * uRadius, y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    float r = length(xz);
    float w = ripples(xz);
    float th = atan(xz.x, -xz.y) + w * 0.03;
    float yr = HZ + 0.3 + (1.0 - r / uRadius) * mix(1.6, 4.0, wrap) + w * 0.12;
    col = sky(th * 0.6, yr) * 0.72 + vec3(0.03, 0.0, 0.06);
    col += 0.025 * sin(r * 22.0 - uT * 0.8) * smoothstep(0.5, 3.0, r);
    col += vec3(1.0, 0.9, 0.8) * max(w, 0.0) * 0.12;
    col *= mix(0.75, 1.0, smoothstep(0.0, uRadius * 0.9, r) * (1.0 - wrap) + wrap);
    local = xz;
  } else {
    float r = length(vPos.xz);
    float th = atan(vPos.x, -vPos.z);
    vec3 dim = mix(vec3(0.34, 0.12, 0.22), vec3(0.2, 0.1, 0.2), n);
    col = mix(dim, sky(th, HZ + 4.0 + (1.0 - r / uRadius) * 3.0), wrap);
    local = vPos.xz;
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  float m = smoothstep(0.0, 0.25, settle * 1.3 - drip * 0.6 - n * 0.25);
  col = mix(col, paint, m);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
